/**
 * The loader: the product's own mark, drawn, filled, held and faded, on a loop.
 *
 * The same mark and the same motion as the splash `index.html` paints before any script runs
 * (`LOADER_MARK` in `src/product/products.ts`, motion in `index.html`'s `#lm-loader-css`), so
 * the hand-over from the splash to the app, and from the app to a page, is one continuous loader.
 * Thunder draws its bolt, Cratus its mark. A product never shows another's.
 *
 * IT JOINS THE LOOP WHERE THE LOOP ALREADY IS (`lib/loader-clock.ts`): a loader created while
 * another is on screen, or just after one was, starts at the same point of the cycle, so replacing
 * one with another does not restart the mark from empty.
 *
 * Under reduced motion the mark is shown whole and still. It still says "loading" to a screen
 * reader, through the status role.
 */
import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { loaderPhase } from '@/lib/loader-clock';
import { LOADER_MARK } from '@/product/products';
import { PRODUCT } from '@/product/profile';

export function BrandLoader({
  size = 48,
  label = 'Loading',
  inline = false,
}: {
  size?: number;
  /** What is loading, for a screen reader: "Loading Command Centre". */
  label?: string;
  /** Sit in a line of text, beside a page title, rather than as a block. */
  inline?: boolean;
}): ReactNode {
  /* The mark's gradient id, made unique: two loaders on a page must not share one, or the
     second's disc paints from the first's, which a browser drops while the first is hidden. */
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  /* Read once, at creation. A style that changed on a re-render would restart the animation. */
  const [phase] = useState(loaderPhase);
  const markup = LOADER_MARK[PRODUCT.id].replaceAll('lm-mark-grad', `lm-mark-grad-${uid}`);
  return (
    <span
      role="status"
      aria-live="polite"
      className={`${inline ? 'inline-flex align-middle' : 'flex'} shrink-0`}
      style={{ width: size, height: size, '--lm-t': `${-Math.round(phase)}ms` } as CSSProperties}
    >
      {/* Our own constant, never anything a person typed. */}
      <span aria-hidden className="block size-full [&>svg]:size-full" dangerouslySetInnerHTML={{ __html: markup }} />
      <span className="sr-only">{label}</span>
    </span>
  );
}
